import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarIcon, MagnifyingGlassIcon, MapPinIcon, ClockIcon } from '@heroicons/react/24/outline';
import PublicLayoutWrapper from '../components/PublicLayoutWrapper';
import { useClassData } from '../hooks/useClassData';
import { calculateCycleDates, getCycleTypeName } from '../utils/classUtils';

const PublicClassesDirectory = () => {
    const { classes: rawClasses, loading } = useClassData(true); // No venue identifier = all public classes
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedVenue, setSelectedVenue] = useState('');
    const navigate = useNavigate();

    // Ensure classes is always an array
    const classes = useMemo(() => {
        if (!rawClasses) return [];
        if (Array.isArray(rawClasses)) return rawClasses;
        // If it's an object, try to extract an array property
        if (typeof rawClasses === 'object') {
            // Common API response patterns
            if (rawClasses.data && Array.isArray(rawClasses.data)) return rawClasses.data;
            if (rawClasses.classes && Array.isArray(rawClasses.classes)) return rawClasses.classes;
            if (rawClasses.items && Array.isArray(rawClasses.items)) return rawClasses.items;
        }
        console.warn('Classes data is not in expected format:', rawClasses);
        return [];
    }, [rawClasses]);

    // Extract unique venues and locations from classes
    const venues = useMemo(() => {
        const venueMap = new Map();
        classes.forEach(venueClass => {
            const venueKey = venueClass.venue?.shortKey || venueClass.venueId || 'unknown';
            if (!venueMap.has(venueKey)) {
                venueMap.set(venueKey, {
                    key: venueKey,
                    name: venueClass.venue?.name || 'Unknown Venue',
                    location: venueClass.venue?.location || venueClass.venue?.city || '',
                    classCount: 0
                });
            }
            venueMap.get(venueKey).classCount++;
        });
        return Array.from(venueMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [classes]);

    const locations = useMemo(() => {
        const locationSet = new Set();
        classes.forEach(venueClass => {
            if (venueClass.venue?.location || venueClass.venue?.city) {
                locationSet.add(venueClass.venue.location || venueClass.venue.city);
            }
        });
        return Array.from(locationSet).sort();
    }, [classes]);

    // Filter classes based on search, location, and venue
    const filteredClasses = useMemo(() => {
        return classes.filter(venueClass => {
            const matchesSearch = !searchTerm ||
                venueClass.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                venueClass.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                venueClass.venue?.name?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesLocation = !selectedLocation ||
                venueClass.venue?.location === selectedLocation ||
                venueClass.venue?.city === selectedLocation;

            const matchesVenue = !selectedVenue ||
                venueClass.venue?.shortKey === selectedVenue ||
                venueClass.venueId === selectedVenue;

            return matchesSearch && matchesLocation && matchesVenue;
        });
    }, [classes, searchTerm, selectedLocation, selectedVenue]);

    // Group classes by venue
    const classesByVenue = useMemo(() => {
        const grouped = {};
        filteredClasses.forEach(venueClass => {
            const venueKey = venueClass.venue?.shortKey || venueClass.venueId || 'unknown';
            if (!grouped[venueKey]) {
                grouped[venueKey] = {
                    venue: venueClass.venue,
                    classes: []
                };
            }
            grouped[venueKey].classes.push(venueClass);
        });
        return grouped;
    }, [filteredClasses]);

    const handleViewVenueClasses = (venueShortKey) => {
        navigate(`/classes/${venueShortKey}`);
    };

    const formatClassTime = (startDate) => {
        const date = new Date(startDate);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatClassDate = (startDate) => {
        const date = new Date(startDate);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <PublicLayoutWrapper title="Public Classes Directory">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                </div>
            </PublicLayoutWrapper>
        );
    }

    return (
        <PublicLayoutWrapper
            title="Public Classes Directory"
            subtitle="Discover fitness classes in your area"
            showBackToHome={true}
        >
            {/* Search and Filter Section */}
            <div className="mb-8 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search Input */}
                    <div className="flex-1 relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search classes, venues, or descriptions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                    </div>

                    {/* Location Filter */}
                    <div className="sm:w-64">
                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                            <option value="">All Locations</option>
                            {locations.map(location => (
                                <option key={location} value={location}>
                                    {location}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Venue Filter - New */}
                    <div className="sm:w-64">
                        <select
                            value={selectedVenue}
                            onChange={(e) => setSelectedVenue(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                            <option value="">All Venues</option>
                            {venues.map(venue => (
                                <option key={venue.key} value={venue.key}>
                                    {venue.name} ({venue.classCount})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Results Count */}
                <div className="text-gray-400 text-sm">
                    Showing {Object.keys(classesByVenue).length} venues with {filteredClasses.length} classes
                </div>
            </div>

            {Object.keys(classesByVenue).length === 0 ? (
                <div className="text-center py-12">
                    <CalendarIcon className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No classes found matching your criteria.</p>
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setSelectedLocation('');
                            setSelectedVenue('');
                        }}
                        className="mt-4 text-emerald-400 hover:text-emerald-300 font-medium"
                    >
                        Clear filters
                    </button>
                </div>
            ) : (
                <div className="space-y-8">
                    {Object.entries(classesByVenue).map(([venueKey, { venue, classes: venueClasses }]) => (
                        <div key={venueKey} className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                            {/* Venue Header */}
                            <div className="bg-gray-800 px-6 py-4 border-b border-gray-700">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-2">
                                            {venue?.name || 'Unknown Venue'}
                                        </h3>
                                        {venue?.location && (
                                            <div className="flex items-center text-gray-300 text-sm">
                                                <MapPinIcon className="h-4 w-4 mr-1" />
                                                {venue.location}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleViewVenueClasses(venueKey)}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                                    >
                                        View All Classes
                                    </button>
                                </div>
                            </div>

                            {/* Classes List */}
                            <div className="p-6">
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    {venueClasses.map(venueClass => {
                                        const nextDate = venueClass.cycleType !== undefined
                                            ? calculateCycleDates(venueClass.startDate, venueClass.cycleType, 1)[0]
                                            : new Date(venueClass.startDate);

                                        return (
                                            <div key={venueClass.id} className="bg-gray-800 rounded-lg p-4 border border-gray-600">
                                                <div className="flex justify-between items-start mb-3">
                                                    <h4 className="font-semibold text-white text-lg">
                                                        {venueClass.name}
                                                    </h4>
                                                    <span className="text-xs px-2 py-1 bg-emerald-600 text-white rounded-full">
                                                        {venueClass.cycleType !== undefined ? getCycleTypeName(venueClass.cycleType) : 'One-time'}
                                                    </span>
                                                </div>

                                                {venueClass.description && (
                                                    <p className="text-gray-300 text-sm mb-3">
                                                        {venueClass.description.length > 100
                                                            ? `${venueClass.description.substring(0, 100)}...`
                                                            : venueClass.description
                                                        }
                                                    </p>
                                                )}

                                                <div className="space-y-2 text-sm text-gray-400">
                                                    <div className="flex items-center">
                                                        <ClockIcon className="h-4 w-4 mr-2" />
                                                        <span>{formatClassDate(nextDate)} at {formatClassTime(nextDate)}</span>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <span className="text-emerald-400 font-medium">
                                                            ${venueClass.cost || '0'}
                                                        </span>
                                                        <span className="text-xs">
                                                            {venueClass.maxParticipants ? `Max ${venueClass.maxParticipants}` : 'No limit'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <Link
                                                    to={`/classes/${venueKey}`}
                                                    className="block w-full mt-4 text-center bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-md font-medium transition-colors"
                                                >
                                                    Book This Class
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Call to Action for Venues */}
            <div className="mt-12 bg-gradient-to-r from-emerald-900/20 to-blue-900/20 border border-emerald-600/30 rounded-lg p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-2">Are you a fitness venue?</h3>
                <p className="text-gray-300 mb-4">
                    Join WorldsHighStreet to list your classes and reach more customers in your area.
                </p>
                <Link
                    to="/create-venue"
                    className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-md font-medium transition-colors"
                >
                    List Your Venue
                </Link>
            </div>
        </PublicLayoutWrapper>
    );
};

export default PublicClassesDirectory;
